import { computeExperienceName } from 'app/components/commons/Experience/utils';
import type { BedCategory } from 'app/typings';
import type { ExperienceCategory } from 'app/typings/experiences';
import type {
  IBed,
  IHotelWithDetails,
  IMergeRoomItem,
} from 'app/typings/migrationTool/HotelWithDetails';

import type { MergeSelection } from './HotelMigration';
import { SelectActionEnum } from './_components/HotelServiceCard/HotelServiceCard';
import type { MergeActionChildService } from './_components/MergeAction/MergeAction';
import type { MergeItem } from './_content/MigrationLayout';

export const getDifferences = (
  child: MergeActionChildService,
  parent?: MergeActionChildService
): string[] => {
  if (!parent) {
    return [];
  }

  const keysToCompare = [
    'name',
    'description',
    'isPublished',
    'price',
    'categoryId',
  ];

  const differences: string[] = [];

  for (const key of keysToCompare) {
    const childValue = (child as unknown as Record<string, unknown>)[key];
    const parentValue = (parent as unknown as Record<string, unknown>)[key];

    if (childValue !== parentValue) {
      differences.push(key);
    }
  }

  // Special case: compare beds by categoryId + quantity
  const isRoom = 'beds' in child && 'beds' in parent;

  if (isRoom) {
    const childBeds = (child as IMergeRoomItem).beds ?? [];
    const parentBeds = (parent as IMergeRoomItem).beds ?? [];

    const formatBeds = (beds: IBed[]) =>
      beds
        .map((bed) => ({
          categoryId: bed.categoryId,
          quantity: bed.quantity,
        }))
        .sort((a, b) => a.categoryId - b.categoryId);

    const childFormatted = formatBeds(childBeds);
    const parentFormatted = formatBeds(parentBeds);

    const bedsAreEqual =
      childFormatted.length === parentFormatted.length &&
      childFormatted.every(
        (bed, i) =>
          bed.categoryId === parentFormatted[i].categoryId &&
          bed.quantity === parentFormatted[i].quantity
      );

    if (!bedsAreEqual) {
      differences.push('beds');
    }
  }

  return differences;
};

export const getTitle = (
  entityLabel: 'Room' | 'Experience' | 'Package' | 'Hotel',
  service?: MergeActionChildService,
  experienceCategories?: ExperienceCategory[]
) => {
  if (!service) {
    return undefined;
  }

  switch (entityLabel) {
    case 'Room':
      return 'name' in service ? service.name : undefined;
    case 'Experience':
      return computeExperienceName({
        category:
          'categoryId' in service
            ? experienceCategories?.find((cat) => cat.id === service.categoryId)
            : undefined,
        quantity: 'quantity' in service ? (service.quantity ?? null) : null,
        brand: 'brand' in service ? (service.brand ?? null) : null,
        duration: 'duration' in service ? (service.duration ?? null) : null,
        durationDescription:
          'durationDescription' in service
            ? (service.durationDescription ?? null)
            : null,
        time: 'time' in service ? (service.time ?? null) : null,
        locations: 'locations' in service ? (service.locations ?? null) : null,
      });

    case 'Package':
      return 'aliasForHotel' in service ? service.aliasForHotel : undefined;
    case 'Hotel':
      return undefined;

    default:
      return undefined;
  }
};

export const getSelectedParentItem = (
  selectedValue: MergeSelection | undefined,
  itemId: number,
  items: MergeItem[],
  entityLabel: 'Room' | 'Experience' | 'Package' | 'Hotel',
  hotel: IHotelWithDetails,
  currencyFormatter: (price?: number) => string,
  bedCategories?: BedCategory[]
): MergeItem | undefined => {
  if (!selectedValue) {
    return undefined;
  }

  switch (selectedValue?.action) {
    case SelectActionEnum.MERGE:
      if (!selectedValue.targetId) {
        return undefined;
      }

      return getParentMatch(
        selectedValue.targetId,
        entityLabel,
        hotel,
        currencyFormatter,
        bedCategories
      );
    case SelectActionEnum.DO_NOT_MERGE:
      return undefined;
    case SelectActionEnum.CREATE_NEW:
      return items.find((item) => item.id === itemId);
    default:
      return undefined;
  }
};

const getParentMatch = (
  value: string | number,
  entityLabel: 'Room' | 'Experience' | 'Package' | 'Hotel',
  hotel: IHotelWithDetails,
  currencyFormatter: (price?: number) => string,
  bedCategories?: BedCategory[]
): MergeItem | undefined => {
  if (typeof value !== 'number') {
    return undefined;
  }

  switch (entityLabel) {
    case 'Room': {
      const room = hotel.rooms.find((r) => r.id === value);

      if (!room) {
        return undefined;
      }

      const bedsList =
        room.beds
          ?.map((bed) => {
            const name = bedCategories?.find(
              (bedCategory) => bedCategory.id === bed.categoryId
            )?.name;

            return name !== undefined
              ? `${name} (${currencyFormatter(bed.price ?? 0)})`
              : undefined;
          })
          .filter((name): name is string => !!name) || [];

      return { ...room, bedsList };
    }

    case 'Experience':
      return hotel.experiences.find((e) => e.id === value);
    case 'Package':
      return hotel.packages.find((p) => p.id === value);
    default:
      return undefined;
  }
};

export const getRoomWithBedsList = (
  room: IMergeRoomItem,
  currencyFormatter: (price?: number) => string,
  bedCategories?: BedCategory[]
) => {
  if (!room.beds || !bedCategories) {
    return room;
  }

  return {
    ...room,
    bedsList: room.beds
      .map((bed) => {
        const name = bedCategories?.find(
          (bedCategory) => bedCategory.id === bed.categoryId
        )?.name;

        return name !== undefined
          ? `${name} (${currencyFormatter(bed.price ?? 0)})`
          : undefined;
      })
      .filter((entry): entry is string => entry !== undefined),
  };
};
